import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import { Box, CircularProgress, type SxProps, type Theme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NEUTRAL } from "@/theme/tokens";
import { EmptyState } from "../molecules/EmptyState";
import { supportsInlinePdfPreview } from "./utils";

export type PdfPreviewProps = {
	/**
	 * Blob URL of the rendered document, or `null` while there is nothing to show.
	 *
	 * Owned by whoever produced it (react-pdf's `usePDF` revokes its own URLs on change and on
	 * unmount) — this component never revokes it. Revoking a URL whose blob a download button still
	 * needs is exactly the bug that causes.
	 */
	url: string | null;
	isLoading?: boolean;
	/** Accessible name for the viewer frame (already translated). */
	title: string;
	hasError?: boolean;
	/** Already-translated. Defaults to `t("download.preview.empty")`. */
	emptyLabel?: ReactNode;
	/** Already-translated. Defaults to `t("download.preview.error")`. */
	errorLabel?: ReactNode;
	/**
	 * Rendered instead of the frame where no inline PDF viewer exists. Defaults to an `EmptyState`
	 * carrying `t("download.preview.unavailable.device")` plus `action`.
	 */
	unsupportedFallback?: ReactNode;
	/** Action slot under any non-frame state (e.g. the download button). */
	action?: ReactNode;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

const CENTERED: SxProps<Theme> = { display: "flex", alignItems: "center", justifyContent: "center", height: "100%" };

/**
 * Read-only PDF preview panel. Sibling of `SpreadsheetPreview` and deliberately sharing its panel,
 * loading, empty and error treatment, so a dialog that previews both formats reads as one surface.
 *
 * Sizes to its parent — callers pass `flex: 1` / a height via `sx`.
 */
export const PdfPreview: FC<PdfPreviewProps> = ({ url, isLoading, title, hasError, emptyLabel, errorLabel, unsupportedFallback, action, dataTestId, sx }) => {
	const { t } = useTranslation();

	const renderContent = () => {
		if (!supportsInlinePdfPreview()) {
			return (
				unsupportedFallback ?? (
					<EmptyState
						dataTestId={dataTestId ? `${dataTestId}-unsupported` : undefined}
						icon={<PictureAsPdf />}
						title={t("download.preview.unavailable.device")}
						action={action}
					/>
				)
			);
		}
		if (hasError) {
			return (
				<EmptyState
					dataTestId={dataTestId ? `${dataTestId}-error` : undefined}
					icon={<PictureAsPdf />}
					title={errorLabel ?? t("download.preview.error")}
					action={action}
				/>
			);
		}
		if (isLoading) {
			return (
				<Box sx={CENTERED} role="status" data-testid={dataTestId ? `${dataTestId}-loading` : undefined}>
					<CircularProgress />
				</Box>
			);
		}
		if (!url) {
			return (
				<EmptyState
					dataTestId={dataTestId ? `${dataTestId}-empty` : undefined}
					icon={<PictureAsPdf />}
					title={emptyLabel ?? t("download.preview.empty")}
					action={action}
				/>
			);
		}
		return (
			<Box
				component="iframe"
				data-testid={dataTestId ? `${dataTestId}-frame` : undefined}
				src={url}
				title={title}
				sx={{ width: "100%", height: "100%", border: "none", display: "block" }}
			/>
		);
	};

	return (
		<Box
			data-testid={dataTestId}
			role="region"
			aria-label={title}
			aria-busy={isLoading}
			sx={[
				{ border: `1px solid ${NEUTRAL[300]}`, borderRadius: 1, overflow: "hidden", position: "relative", minWidth: 0, minHeight: 0 },
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{renderContent()}
		</Box>
	);
};
